import React, { forwardRef } from 'react';
import { Heading3, Caption } from '../typography';
import { Icon } from '../icon';
import { StyledModal, ModalHeader, ModalContent, ModalFooter, StyledIconButton } from './styled';
import { useBackgroundClick } from './useBackgroundClick';
/**
 * @typedef {Object} IModalProps
 * @property {string} [caption] - Defines caption under the title.
 */
/**
 * @typedef {Object} IModalProps
 * @property {boolean} isOpen - Defines is modal window open or not.
 * @property {function} onClose - Defines function, called on close button click.
 * @property {JSX.Element | string} [title] - Defines title of modal component. Can be string or custom component.
 * @property {string} [caption] - Defines caption under the title.
 * @property {ReactNode} [actions] - Defines action elements, placed in modal header.
 * @property {boolean} [allowScroll] - Defines background page scroll when modal open.
 * @property {Object} [backgroundProps] - Defines props passed to background of modal component.
 * @property {function} [afterOpen] - Defines function, called after modal component open.
 * @property {function} [afterClose] - Defines function, called after codal component close.
 * @property {Promise<void> | function} [beforeOpen] - Defines function or promise, called before modal component open.
 * @property {Promise<void> | function} [beforeClose] - Defines function or promise, called before modal component close.
 * @property {function} [onEscapeKeydown] - Defines function, called on `esc` kay pressed.
 * @property {function} [onBackgroundClick] - Defines function. called on modal background click.
 * @property {ReactNode} [headerAs] - Defines custom element rendered instead of default header block.
 * @property {ReactNode} [contentAs] - Defines custom element rendered instead of default content block.
 * @property {ReactNode} [footerAs] - Defines custom element rendered instead of default footer block.
 * @property {boolean} [hideCloseButton] - Defines visibility of close button in the header of modal.
 * @property {string} [className] - Defines custom css class, that passed to root element of modal component. Used for styles overrides.
 */
export const Modal = forwardRef(function Modal({ title, caption, actions, children, hideCloseButton, onClose, headerAs, contentAs, footerAs, ...props }, ref) {
    const ModalHeaderComponent = headerAs !== null && headerAs !== void 0 ? headerAs : ModalHeader;
    const ModalContentComponent = contentAs !== null && contentAs !== void 0 ? contentAs : ModalContent;
    const ModalFooterComponent = footerAs !== null && footerAs !== void 0 ? footerAs : ModalFooter;
    const { backgroundProps, onBackgroundClick } = useBackgroundClick(props);
    return (React.createElement(StyledModal, { "data-testid": "modal", ref: ref, ...props, backgroundProps: backgroundProps, onBackgroundClick: onBackgroundClick },
        headerAs ? (React.createElement(ModalHeaderComponent, { "data-testid": "header" })) : (React.createElement(ModalHeader, null,
            title && (React.createElement("header", null,
                typeof title === 'string' ? (React.createElement(Heading3, { "$shade": "mediumEmphasis" }, title)) : (title),
                caption && React.createElement(Caption, { "$shade": "mediumEmphasis" }, caption))),
            hideCloseButton ? null : (React.createElement(StyledIconButton, { "data-testid": "close", onClick: onClose },
                React.createElement(Icon, { name: "close" }))))),
        React.createElement(ModalContentComponent, { "data-testid": "body" }, children),
        actions && React.createElement(ModalFooterComponent, { "data-testid": "actions" }, actions)));
});
